import { FC, useCallback, useMemo } from 'react';
import { Pictogram } from '@tuic/web-compositions/core/Pictogram';
import { GeneralView } from '@modules/get-slash-internet-card/components/dialog-views/general-view';
import { ConsumptionProgress } from '@modules/get-slash-internet-card/components/dialog-views/consumption-view/consumption-progress';
import { GetSlashInternetSession } from '@modules/get-slash-internet-card/lib/use-get-slash-internet';
import { InternetCardViewData } from '@modules/get-slash-internet-card/lib/internet-card-view-data';
import {
  DocumentRenderer,
  TextRenderer,
} from '@modules/get-slash-internet-card/lib/utils';
import { useClientTranslation } from '@lib/i18n/client';
import {
  ViewStack,
  ViewStackContext,
} from '@modules/get-slash-internet-card/components/dialog-views/view-stack';
import { TariffListing } from '@modules/get-slash-internet-card/components/dialog-views/booking-view/tariff-listing';
import { Locale } from '@tuic/i18n';
import { GetSlashCredentials } from '@lib/third-parties/get-slash.types';
import { RefreshDialogHandler } from '@modules/get-slash-internet-card/components/dialog-view-renderer';

type ConsumptionViewProps = {
  data: InternetCardViewData['exhaustedView'];
  session: GetSlashInternetSession;
  documentRenderer: DocumentRenderer;
  textRenderer: TextRenderer;
  locale: Locale;
  getSlashCredentials: GetSlashCredentials;
  useDebugEndpoints?: boolean;
  refreshDialog: RefreshDialogHandler;
};

export const ExhaustedView: FC<ConsumptionViewProps> = ({
  data,
  session,
  documentRenderer,
  textRenderer,
  locale,
  getSlashCredentials,
  useDebugEndpoints,
  refreshDialog,
}) => {
  const { t } = useClientTranslation();

  const progressBar = useMemo(() => {
    if (typeof session.maxVolume === 'undefined') {
      return null;
    }

    return <ConsumptionProgress session={session} />;
  }, [session]);

  const showTariffList = useCallback(
    (push: ViewStackContext['push']) => {
      push(
        <TariffListing
          locale={locale}
          useDebugEndpoints={useDebugEndpoints}
          getSlashCredentials={getSlashCredentials}
          tariffs={data?.tariffs}
          fallbackImages={data?.fallbackImages}
          refreshDialog={refreshDialog}
        />,
      );
    },
    [
      getSlashCredentials,
      locale,
      useDebugEndpoints,
      data?.tariffs,
      data?.fallbackImages,
      refreshDialog,
    ],
  );

  return (
    <ViewStack>
      {({ push }) => (
        <GeneralView
          centered
          title={textRenderer(data?.title)}
          text={documentRenderer(data?.text)}
          connectionTag={
            <Pictogram name="wifi" size="md" className="block mx-auto" />
          }
          progressBar={progressBar}
          buttons={[
            {
              children:
                data?.buttonLabels?.discoverTariffs ?? t('Tarife entdecken'),
              variant: 'primary',
              onClick: () => showTariffList(push),
            },
          ]}
        />
      )}
    </ViewStack>
  );
};
